import { Fragment, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardPanel } from '@/components/dashboard/dashboard-panel';
import { Badge } from '@/components/ui/badge';
import { percent } from '@/components/analytics/objections/objection-view-model';
import { VoiceRowDetails } from './VoiceRowDetails';
import { VoiceTopList } from './VoiceTopList';
import { isSmallSample, type VoiceRowItem } from './voice-view-model';
import type { DeclineBreakdownRow } from './voice-questions-view-model';

/** A number of the panel that opens to its conversations (#1641). */
interface Cell {
  /**
   * The cell's logical identity — its row's id and its reason — so a refetch
   * that reorders the rows keeps the same cell open. Unique within the panel.
   */
  key: string;
  /** The visible text — also the start of the button's accessible name. */
  text: string;
  calls: number;
  items?: VoiceRowItem[];
  /** What an empty wording says in the details (`VoiceRowDetails`). */
  emptyWording?: string | null;
}

/**
 * Разбивка отказов: строка — сколько отказов (из N отказов окна) и какие
 * причины. Каждое число раскрывается до своих разговоров (#1641) тем же
 * раскрытием, что строки «Почему отказываются» (`VoiceRowDetails`): клетка
 * причины, «причина не названа» и строки под панелью (сотрудники ниже порога
 * разговоров, «без сотрудника»).
 * Итог строки — сумма её клеток и сам не раскрывается. Кнопка клетки — единственный
 * переключатель раскрытия; открыта одна клетка панели. Число без разговоров
 * в ответе (edge-api до #1641) остаётся числом.
 */
export function DeclineBreakdownPanel({
  title,
  hint,
  rows,
  declines,
  emptyText,
  notes = [],
  onOpenCall,
  limit,
}: {
  title: string;
  hint?: string;
  rows: readonly DeclineBreakdownRow[];
  declines: number;
  /** What no row means here, when it is not "no declines". */
  emptyText?: string;
  /**
   * Lines under the rows: the declines the rows leave out, and their
   * conversations. Shown with no rows too — every decline of the window may
   * be one the rows leave out. `key` is unique among the notes.
   */
  notes?: readonly { key: string; text: string; calls: number; items?: VoiceRowItem[] }[];
  onOpenCall: (recordId: string) => void;
  /**
   * The rows with the most declines first, then «Show all N» (#2060): the
   * employee and stage cuts. The call-number cut has fixed buckets and none.
   */
  limit?: number;
}) {
  const { t } = useTranslation('dashboard');
  const baseId = useId();
  const [openCell, setOpenCell] = useState<string | null>(null);
  // A row id carries free text (an employee id, a CRM stage key): encoded, it
  // has no whitespace, so it stays one token of `aria-controls`.
  const detailsId = (cell: Cell) => `${baseId}-${encodeURIComponent(cell.key)}`;
  /** A cell of a row: the row's id and the reason, `null` — no reason named. */
  const cellKey = (row: DeclineBreakdownRow, code: string | null) =>
    JSON.stringify([row.id, code]);

  const cellControl = (cell: Cell) => {
    if (!cell.items?.length) return <span key={cell.key}>{cell.text}</span>;
    const expanded = openCell === cell.key;
    return (
      <button
        key={cell.key}
        type="button"
        className="rounded-sm text-left text-primary underline-offset-2 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-expanded={expanded}
        aria-controls={expanded ? detailsId(cell) : undefined}
        aria-label={t(
          expanded ? 'voicePage.declines.cellHide' : 'voicePage.declines.cellShow',
          { label: cell.text }
        )}
        onClick={() => setOpenCell(expanded ? null : cell.key)}
      >
        {cell.text}
      </button>
    );
  };

  const cellDetails = (cells: readonly Cell[]) => {
    const open = cells.find((cell) => cell.key === openCell && cell.items?.length);
    if (!open?.items) return null;
    return (
      <div className="mt-2">
        <VoiceRowDetails
          id={detailsId(open)}
          row={{ kind: 'decline', calls: open.calls, items: open.items }}
          onOpenCall={onOpenCall}
          emptyWording={open.emptyWording}
        />
      </div>
    );
  };

  // A note's key never starts with «[»: no clash with a row cell (`cellKey`).
  const noteCells: Cell[] = notes.map((note) => ({
    key: `note:${note.key}`,
    text: note.text,
    calls: note.calls,
    items: note.items,
    emptyWording: null,
  }));

  return (
    <DashboardPanel
      title={title}
      action={
        <span className="text-xs text-fg-muted">
          {t('voicePage.declines.windowCalls', { count: declines })}
        </span>
      }
    >
      {hint && <p className="mb-3 text-xs text-fg-muted">{hint}</p>}
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-fg-muted">
          {emptyText ?? t('voicePage.declines.breakdownEmpty')}
        </p>
      ) : (
        <VoiceTopList
          list="ul"
          rows={limit === undefined ? rows : [...rows].sort((a, b) => b.calls - a.calls)}
          limit={limit}
          rowKey={(row) => row.id}
          calls={(row) => row.calls}
          renderRow={(row) => {
            const cells: Cell[] = [
              ...row.reasons.map((reason) => ({
                key: cellKey(row, reason.code),
                text: t('voicePage.declines.reasonCount', {
                  reason: reason.label,
                  count: reason.calls,
                }),
                calls: reason.calls,
                items: reason.items,
              })),
              ...(row.withoutReason > 0
                ? [
                    {
                      key: cellKey(row, null),
                      text: t('voicePage.declines.withoutReason', {
                        count: row.withoutReason,
                      }),
                      calls: row.withoutReason,
                      items: row.withoutReasonItems,
                      emptyWording: t('voicePage.declines.noReasonWording'),
                    },
                  ]
                : []),
            ];
            return (
              <li
                key={row.id}
                className="rounded-card border border-border bg-bg-surface p-3"
              >
                <div className="flex items-center gap-3">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">
                    {row.label}
                  </span>
                  {isSmallSample(row.calls) && (
                    <span title={t('voicePage.smallSample')}>
                      <Badge variant="default">{t('voicePage.smallSampleBadge')}</Badge>
                    </span>
                  )}
                  <span className="font-mono text-xs text-fg-muted">
                    {t('voicePage.declines.calls', {
                      calls: row.calls,
                      count: declines,
                      share: percent(row.calls, declines),
                    })}
                  </span>
                </div>
                {cells.length > 0 && (
                  <p className="mt-1 text-xs text-fg-muted">
                    {cells.map((cell, index) => (
                      <Fragment key={cell.key}>
                        {index > 0 && ' · '}
                        {cellControl(cell)}
                      </Fragment>
                    ))}
                  </p>
                )}
                {cellDetails(cells)}
              </li>
            );
          }}
        />
      )}
      {noteCells.map((noteCell) => (
        <div key={noteCell.key} className="mt-3">
          <p className="text-xs text-fg-muted">{cellControl(noteCell)}</p>
          {cellDetails([noteCell])}
        </div>
      ))}
    </DashboardPanel>
  );
}
